import { useId, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError, confirmQuery, nameQuery } from "@/api/client";
import { recallObserver, rememberObserver } from "@/lib/observer";
import { to } from "@/site/parts";

function describe(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return `${error.message} This photograph cannot be used to name a shark.`;
  }
  return error instanceof Error ? error.message : String(error);
}

export function CertificateLink({ id, message }: { id: string; message: string }) {
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <p className="muted">{message}</p>
      <Link to={to.certificate(id)} className="btn" style={{ justifySelf: "start" }}>
        Open the certificate <span className="btn-arrow">→</span>
      </Link>
    </div>
  );
}

/** Confirm a candidate: asks for the observer's name, logs the sighting, opens the certificate. */
export function LogSightingForm({
  queryId,
  sharkId,
  sharkName,
}: {
  queryId: string;
  sharkId: string;
  sharkName: string;
}) {
  const navigate = useNavigate();
  const id = useId();
  const [observer, setObserver] = useState(recallObserver);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const name = observer.trim();
    if (!name) {
      setError("Observer required: the name is printed on the certificate.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      rememberObserver(name);
      const certificate = await confirmQuery(queryId, sharkId, name);
      navigate(to.certificate(certificate.id));
    } catch (err) {
      setBusy(false);
      setError(`The sighting was not logged: ${describe(err)}`);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate style={{ display: "grid", gap: 18 }}>
      <label className="field" htmlFor={`${id}-observer`}>
        <span className="label">Observer · printed on the certificate</span>
        <input
          id={`${id}-observer`}
          className="input"
          value={observer}
          autoComplete="name"
          placeholder="Ana Ruiz"
          maxLength={80}
          onChange={(e) => setObserver(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
      </label>
      <button type="submit" className="btn" disabled={busy}>
        {busy ? "Logging…" : `Log sighting of ${sharkId}`}
        <span className="btn-arrow" aria-hidden="true">
          →
        </span>
        <span className="sr-only"> ({sharkName})</span>
      </button>
      {error && (
        <p id={`${id}-error`} role="alert" className="alert">
          {error}
        </p>
      )}
    </form>
  );
}

/** Name a shark that is new to the registry: the discovery. */
export function NameSharkForm({ queryId }: { queryId: string }) {
  const navigate = useNavigate();
  const id = useId();
  const [name, setName] = useState("");
  const [observer, setObserver] = useState(recallObserver);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const sharkName = name.trim();
    const observerName = observer.trim();
    if (!sharkName || !observerName) {
      setError(sharkName ? "Finder required: you are credited on the record." : "Name required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      rememberObserver(observerName);
      const certificate = await nameQuery(queryId, sharkName, observerName);
      navigate(to.certificate(certificate.id));
    } catch (err) {
      setBusy(false);
      setError(`The shark was not named: ${describe(err)}`);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate style={{ display: "grid", gap: 22 }}>
      <label className="field" htmlFor={`${id}-name`}>
        <span className="label">Designation · name this individual</span>
        <input
          id={`${id}-name`}
          className="input input-xl"
          value={name}
          placeholder="Stella Maris"
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="field" htmlFor={`${id}-observer`}>
        <span className="label">Finder · credited on the record</span>
        <input
          id={`${id}-observer`}
          className="input"
          value={observer}
          autoComplete="name"
          placeholder="Ana Ruiz"
          maxLength={80}
          onChange={(e) => setObserver(e.target.value)}
        />
      </label>
      <button type="submit" className="btn" disabled={busy}>
        {busy ? "Entering…" : "Enter into the registry"}
        <span className="btn-arrow" aria-hidden="true">
          →
        </span>
      </button>
      {error && (
        <p role="alert" className="alert">
          {error}
        </p>
      )}
    </form>
  );
}
